import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { IHttpResponse, IHttpStatus } from '@partners/api';
import { ErrorsService, RemoteError } from '@partners/errors';
import { catchError } from 'rxjs/operators';

import { ShellStore } from '@partners/state';
import { ITrackerHttpContext, TRACKER_HTTP_CONTEXT } from '@partners/worker';
import { of } from 'rxjs';

/**
 * @description
 * catches all remote (http) errors, which means all server responses with http erroneous status
 * (status > 299 or status < 200).
 * the erroneous status values indicates either:
 *    (1) a technical error (server unavailable | page not found | no internet connection etc')
 *    (2) business logic error (missing data | miscalculation | refusal to service etc')
 *
 * @param request the current http request
 * @param next a method of propagating the request forward to allow interception
 * @returns the next tick in the request process
 */

export const remoteErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const _errorsService = inject(ErrorsService);
  const _shellStore = inject(ShellStore);

  return next(request).pipe(
    /** catch any http errors */
    catchError((httpError: HttpErrorResponse) => {
      const { error } = httpError;
      const { status } = error as IHttpResponse<any>;

      if (!status) {
        return _errorsService.throwRemoteError({
          code: '',
          message: error?.message,
          abort: true,
          response: error,
          request
        });
      }

      const { message, messageCode, headlineMessage, continueProcess } = status as IHttpStatus;

      // if is it an tracker exeption, swallow error.
      if (ignoreTrackerError(request)) return of(); // swallow any tracker error

      if (messageCode === 'E_0100') return of();

      const remoteError = new RemoteError({
        code: error.status,
        message: message,
        title: headlineMessage,
        abort: !continueProcess,
        response: error,
        request
      });

      _shellStore.dispatchSaveError(remoteError);

      return _errorsService.throwRemoteError(remoteError);
    })
  );
};

// const context: IHttpContext = ContextBuilder.getContext(request);
// const { resolvers } = context;

const ignoreTrackerError = (request: HttpRequest<any>): boolean => {
  const trackerContext: ITrackerHttpContext = request.context.get(TRACKER_HTTP_CONTEXT);

  if (trackerContext.ignoreServerErrors) {
    return trackerContext.ignoreServerErrors as boolean;
  }

  return false;
};